import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCreatePasteMutation } from '../api/generated';
import { useAppDispatch } from '../app/hooks';
import { LanguageSelect } from '../components/LanguageSelect';
import { PasteEditor } from '../components/PasteEditor';
import { ProblemMessage } from '../components/ProblemMessage';
import { StdinEditor } from '../components/StdinEditor';
import { TerminalDrawer } from '../components/TerminalDrawer';
import { useLanguages } from '../components/useLanguages';
import { Workspace } from '../components/Workspace';
import { pasteCreated, previewOf } from '../features/owned/ownedPastesSlice';

/** Matches the backend's Title schema. */
const TITLE_MAX_LENGTH = 200;

export function CreatePastePage() {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [stdin, setStdin] = useState('');
  const formId = useId();
  const languages = useLanguages();
  const [chosenLanguage, setChosenLanguage] = useState<string | undefined>(undefined);
  const language = chosenLanguage ?? languages.defaultLanguage;
  const [createPaste, { isLoading, error }] = useCreatePasteMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const submit = async () => {
    const trimmed = title.trim();
    const request = {
      content,
      language,
      ...(trimmed === '' ? {} : { title: trimmed }),
      ...(stdin === '' ? {} : { stdin }),
    };
    const result = await createPaste({ createPasteRequest: request });
    if (result.data !== undefined) {
      const { id, deleteToken, createdAt } = result.data;
      const created = { id, deleteToken, createdAt, preview: previewOf(content) };
      dispatch(pasteCreated(result.data.title === undefined ? created : { ...created, title: result.data.title }));
      await navigate(`/p/${id}`);
    }
  };

  return (
    <Workspace
      title="New paste"
      terminal={
        <TerminalDrawer tabs={[{ id: 'stdin', label: 'stdin', content: <StdinEditor value={stdin} onChange={setStdin} /> }]} activeTab="stdin" onSelect={() => {}} />
      }
      heading={
        <input
          className="drawer-title drawer-title-input"
          type="text"
          form={formId}
          aria-label="Paste title"
          placeholder="New paste"
          maxLength={TITLE_MAX_LENGTH}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      }
      options={
        <form
          id={formId}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <LanguageSelect
            value={language}
            options={languages.options}
            onChange={setChosenLanguage}
            disabled={languages.isLoading}
          />
          {languages.loadFailed && (
            <p className="problem" role="status">
              <small>
                Could not load the language list; retrying.{' '}
                <button type="button" className="link-button" onClick={languages.retry}>
                  Retry now
                </button>
              </small>
            </p>
          )}
          <ProblemMessage error={error} />
          <button type="submit" disabled={content.length === 0 || isLoading} aria-busy={isLoading}>
            Create paste
          </button>
        </form>
      }
    >
      <PasteEditor value={content} language={language} label="Paste content" onChange={setContent} />
    </Workspace>
  );
}
